/**
 * @file surface-fill.ts
 * @description Заливка шапки и подвала сайта: ролью цвета темы или своим
 * цветом (решение пользователя 2026-10-11, дополнение SB8; файл фазы
 * phase19.16 §7.17, последний абзац).
 *
 * Зачем отдельный модуль: заливку читают схема темы (schema.ts), CSS
 * (css.ts — `--site-header-bg` / `--site-footer-bg`), «Что изменено»
 * (diff.ts), проверки контраста кабинета и фон секции «Свой цвет»
 * (site-section). Одна функция `resolveSurfaceFill` — один ответ «какой фон
 * и светлый он или тёмный» для всех.
 *
 * Тон (`header.tone`, `footer.tone`) остаётся в документе для прежних
 * потребителей, но становится производным от заливки: `readSiteTheme`
 * пересчитывает его через `syncSurfaceTones`. Исключение — прозрачная шапка
 * (`tone: "transparent"`): прозрачность — отдельный режим, а заливка в нём
 * задаёт вид шапки после прокрутки и на страницах без фото.
 *
 * Старые темы без `fill` получают заливку, равную прежнему тону
 * (`deriveMissingSurfaceFills`, до слияния с умолчаниями): так на витрине
 * сейчас рисуются шапка (светлая — фон страницы) и подвал (светлый —
 * карточки), тёмные — «Тёмный фон». Записей о замене при этом нет.
 */

import { z } from "zod";

import { compositeColors, contrastRatio } from "../cabinet-theme/contrast.js";
import { requireHexColor, toHexColor, WHITE } from "./color.js";
import { resolveSurface, SITE_CONTRAST_TEXT_MIN } from "./contrast.js";
import { SITE_COLOR_PATTERN, type SiteColorRole, type SiteColors } from "./keys.js";

/**
 * Роли, которые годятся в заливку: поверхности и акцент (как фон секции —
 * SECTION_BG_ROLES). Роли текста заливкой не бывают.
 */
export const SITE_FILL_ROLES = [
  "pageBg",
  "cardBg",
  "panelBg",
  "darkBg",
  "accent",
] as const satisfies readonly SiteColorRole[];
export type SiteFillRole = (typeof SITE_FILL_ROLES)[number];

/** Свой цвет заливки — тот же формат, что у ролей темы (#rrggbb или #rrggbbaa). */
export const siteFillColorSchema = z
  .string()
  .regex(SITE_COLOR_PATTERN, "Цвет — в формате #rrggbb или #rrggbbaa");

export const siteSurfaceFillSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), role: z.enum(SITE_FILL_ROLES) }),
  z.strictObject({ kind: z.literal("custom"), color: siteFillColorSchema }),
]);
export type SiteSurfaceFill = z.infer<typeof siteSurfaceFillSchema>;

/** Тон поверхности: светлая (текст — цветом «Текст») или тёмная (текст — «На тёмном»). */
export type SiteSurfaceTone = "light" | "dark";

/**
 * Заливка, равная прежнему тону: так витрина рисовала шапку и подвал до
 * появления заливки (SiteHeaderBar / SiteFooter, сверено 2026-10-11).
 */
export const SURFACE_FILL_BY_TONE: Readonly<{
  header: Readonly<Record<"light" | "dark" | "transparent", SiteSurfaceFill>>;
  footer: Readonly<Record<"light" | "dark", SiteSurfaceFill>>;
}> = {
  header: {
    light: { kind: "role", role: "pageBg" },
    dark: { kind: "role", role: "darkBg" },
    // Прозрачная шапка после прокрутки и без фото — светлая, на фоне страницы.
    transparent: { kind: "role", role: "pageBg" },
  },
  footer: {
    light: { kind: "role", role: "cardBg" },
    dark: { kind: "role", role: "darkBg" },
  },
};

/**
 * Тон ролей-поверхностей фиксирован — как витрина рисовала их до заливки
 * (контраст «Текста» и «На тёмном» к своим фонам проверяют правила
 * SITE_CONTRAST_RULES). Акцент и свой цвет — по контрасту.
 */
const FIXED_ROLE_TONE: Readonly<Partial<Record<SiteFillRole, SiteSurfaceTone>>> = {
  pageBg: "light",
  cardBg: "light",
  panelBg: "light",
  darkBg: "dark",
};

/**
 * Тон произвольного непрозрачного фона: какой из текстов темы («Текст» или
 * «На тёмном») на нём контрастнее. Это и есть «по светлоте», но в цветах
 * магазина: при светлом «На тёмном» и тёмном «Тексте» совпадает с
 * яркостью фона, при необычной палитре — выбирает читаемый вариант.
 */
export function surfaceToneOf(background: string, colors: SiteColors): SiteSurfaceTone {
  const bg = opaqueOnPage(background, colors);
  const onLight = contrastRatio(compositeColors(requireHexColor(colors.text), bg), bg);
  const onDark = contrastRatio(compositeColors(requireHexColor(colors.onDark), bg), bg);
  return onDark > onLight ? "dark" : "light";
}

/** Итог заливки: непрозрачный фон (`#rrggbb`) и тон поверхности. */
export interface ResolvedSurfaceFill {
  background: string;
  tone: SiteSurfaceTone;
}

/**
 * Фон и тон заливки по цветам темы. Принимает тему целиком или только её
 * цвета (`{ colors }`) — кабинету удобно передавать черновик. Полупрозрачный
 * цвет лежит на фоне страницы (как роли в `resolveSurface`).
 */
export function resolveSurfaceFill(
  fill: SiteSurfaceFill,
  theme: { readonly colors: SiteColors },
): ResolvedSurfaceFill {
  const { colors } = theme;
  if (fill.kind === "role") {
    const background = toHexColor(resolveSurface(colors, fill.role));
    return { background, tone: FIXED_ROLE_TONE[fill.role] ?? surfaceToneOf(background, colors) };
  }
  const background = toHexColor(opaqueOnPage(fill.color, colors));
  return { background, tone: surfaceToneOf(background, colors) };
}

/** Контраст текста на заливке: число и «читается ли обычный текст» (WCAG AA 4,5). */
export interface SurfaceFillContrast {
  ratio: number;
  ok: boolean;
}

/**
 * Контраст текста шапки/подвала на заливке — для подсказки кабинета и
 * проверок окна публикации. `null` — проверять нечего сверх обычного:
 * роли-поверхности (фон страницы, карточки, плашка, тёмный фон) уже
 * проверены правилами контраста темы (SITE_CONTRAST_RULES), повторять
 * предупреждение незачем; а также битый цвет черновика.
 */
export function fillContrastIssue(
  fill: SiteSurfaceFill,
  theme: { readonly colors: SiteColors },
): SurfaceFillContrast | null {
  if (fill.kind === "role" && FIXED_ROLE_TONE[fill.role] !== undefined) return null;
  try {
    const { background, tone } = resolveSurfaceFill(fill, theme);
    const bg = requireHexColor(background);
    const text = requireHexColor(tone === "light" ? theme.colors.text : theme.colors.onDark);
    const ratio = contrastRatio(compositeColors(text, bg), bg);
    return { ratio, ok: ratio >= SITE_CONTRAST_TEXT_MIN };
  } catch {
    return null;
  }
}

/** Цвет на фоне страницы (фон страницы — на белом) → непрозрачный. */
function opaqueOnPage(color: string, colors: SiteColors): ReturnType<typeof requireHexColor> {
  const parsed = requireHexColor(color);
  if (parsed.a >= 1) return parsed;
  const page = compositeColors(requireHexColor(colors.pageBg), WHITE);
  return compositeColors(parsed, page);
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Старая тема без `fill`: заливка из прежнего тона — ДО слияния с
 * умолчаниями (иначе тёмная шапка получила бы светлую заливку умолчания).
 * Неизвестный тон не трогаем: часть не пройдёт схему и целиком станет
 * умолчанием. Меняет переданный объект — вызывать на копии.
 */
export function deriveMissingSurfaceFills(doc: unknown): void {
  if (!isPlainObject(doc)) return;
  for (const part of ["header", "footer"] as const) {
    const section = doc[part];
    if (!isPlainObject(section) || "fill" in section) continue;
    const tone = section["tone"];
    const byTone = SURFACE_FILL_BY_TONE[part] as Readonly<Record<string, SiteSurfaceFill>>;
    if (typeof tone === "string" && Object.prototype.hasOwnProperty.call(byTone, tone)) {
      section["fill"] = { ...byTone[tone]! };
    }
  }
}

/** Части темы, у которых тон производный от заливки. */
interface SurfaceParts {
  readonly colors: SiteColors;
  readonly header: { readonly tone: "light" | "dark" | "transparent"; readonly fill: SiteSurfaceFill };
  readonly footer: { readonly tone: "light" | "dark"; readonly fill: SiteSurfaceFill };
}

const sameFill = (a: SiteSurfaceFill, b: SiteSurfaceFill): boolean =>
  a.kind === b.kind && (a.kind === "role" ? a.role === (b as typeof a).role : a.color === (b as typeof a).color);

/**
 * Согласовать тон и заливку одной части. Совпадают — как есть. Расходятся:
 *   - заливка — роль «по тону» (светлая или тёмная для этой части, как
 *     SURFACE_FILL_BY_TONE) → прав ТОН: так пишет кабинет, который пока
 *     переключает только тон; заливка становится заливкой этого тона;
 *   - свой цвет, акцент или другая роль → права ЗАЛИВКА, тон пересчитывается.
 * Контракт для кабинета: выбирая заливку, писать и тон
 * (`resolveSurfaceFill(fill, theme).tone`) — тогда расхождений нет.
 */
function reconcile<F extends { readonly tone: string; readonly fill: SiteSurfaceFill }>(
  section: F,
  byTone: Readonly<Record<"light" | "dark", SiteSurfaceFill>>,
  colors: SiteColors,
): F {
  if (section.tone !== "light" && section.tone !== "dark") return section;
  const resolved = resolveSurfaceFill(section.fill, { colors }).tone;
  if (resolved === section.tone) return section;
  const toneDefault = sameFill(section.fill, byTone.light) || sameFill(section.fill, byTone.dark);
  return toneDefault ? { ...section, fill: { ...byTone[section.tone] } } : { ...section, tone: resolved };
}

/**
 * Тон шапки и подвала согласован с заливкой (`reconcile`); прозрачная шапка
 * остаётся прозрачной. Возвращает ту же тему, если менять нечего.
 */
export function syncSurfaceTones<T extends SurfaceParts>(theme: T): T {
  const header = reconcile(theme.header, SURFACE_FILL_BY_TONE.header, theme.colors);
  const footer = reconcile(theme.footer, SURFACE_FILL_BY_TONE.footer, theme.colors);
  if (header === theme.header && footer === theme.footer) return theme;
  return { ...theme, header, footer };
}
